import React from 'react';
import { formatClientPricingMoney } from '../../utils/clientPricingMatrix';

const getRowId = (row) => Number(row?.varietyPriceId);

const getRuleLabel = (rule) => {
  const value = Number(rule?.ruleValue);
  if (!Number.isFinite(value)) return '';
  const label = rule?.action === 'set_benefit' ? 'Margen' : 'Descuento';
  return rule?.ruleType === 'percent'
    ? `${label} ${value}%`
    : `${label} ${formatClientPricingMoney(value)}`;
};

const REASON_LABELS = {
  missing_weight: 'Falta peso',
  missing_cost: 'Falta costo',
  benefit_above_base: 'Margen sobre precio base',
  invalid_rule_value: 'Valor de regla invalido',
};

const PendingClientPricingCosts = ({
  rows = [],
  costValues = {},
  savingById = {},
  errorsById = {},
  rule = null,
  disabled = false,
  canUpdateWeight = true,
  canUpdateCost = true,
  onCostChange,
  onSave,
}) => {
  if (!rows.length) return null;
  const ruleLabel = getRuleLabel(rule);

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-amber-950">
            Cambios pendientes ({rows.length})
          </h3>
          <p className="mt-1 text-sm text-amber-800">
            El cambio ya se aplico a todos los precios permitidos. Corrige estos casos y aplicalos individualmente.
          </p>
        </div>
        {ruleLabel && (
          <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200">
            {ruleLabel}
          </span>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {rows.map((row) => {
          const id = getRowId(row);
          const saving = Boolean(savingById[id]);
          const draft = costValues[id] || {};
          const requiresWeight = row.reason === 'missing_weight';
          const requiresCost = row.reason === 'missing_cost';
          const canEditWeight = requiresWeight || row?.referenceCost?.calculationMethod === 'unit_weight_kg';
          const canEditCost = requiresCost
            || row?.referenceCost?.calculationMethod === 'estimated'
            || row?.referenceCost?.referenceCostSource === 'estimated';
          const hasRequiredReference = requiresWeight
            ? canUpdateWeight && Number(draft.weight) > 0
            : requiresCost
              ? canUpdateCost && String(draft.cost ?? '').trim() !== '' && Number(draft.cost) >= 0
              : true;
          const hasRuleValue = Number(draft.ruleValue) > 0;
          const action = row.action || rule?.action || 'set_benefit';
          const ruleName = action === 'set_benefit' ? 'Margen' : 'Descuento';
          const rowLabel = `${row.productName || 'Producto'} · ${row.varietyName || 'Variedad'}`;
          const formatLabel = [row.productFormatName, row.metricUnit, row.description]
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={id} className="rounded-lg border border-amber-200 bg-white p-3">
              <div className="grid gap-3 lg:grid-cols-[minmax(180px,1fr)_minmax(150px,190px)_minmax(210px,260px)_auto] lg:items-end">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">{rowLabel}</p>
                  {formatLabel && <p className="mt-1 text-xs text-gray-500">{formatLabel}</p>}
                  <p className="mt-1 text-xs text-gray-500">
                    Precio base: {formatClientPricingMoney(row.basePrice)}
                  </p>
                  <span className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                    {REASON_LABELS[row.reason] || 'Requiere revision'}
                  </span>
                  <p className="mt-1 text-xs text-amber-800">{row.message}</p>
                  {row.reason === 'benefit_above_base' && (
                    <p className="mt-1 text-xs text-gray-500">
                      Margen maximo: {formatClientPricingMoney(row.maxBenefitAmount)} ({Number(row.maxBenefitPercent || 0).toLocaleString('de-DE', { maximumFractionDigits: 1 })}%)
                    </p>
                  )}
                </div>
                <div>
                  {canEditWeight && (
                    <>
                      <label className="text-xs font-semibold text-gray-700">
                        Peso de unidad
                        <span className="mt-1 flex items-center gap-1">
                          <input
                            aria-label={`Peso de unidad para ${rowLabel}`}
                            type="number"
                            min="0"
                            step="any"
                            inputMode="decimal"
                            value={draft.weight ?? ''}
                            onChange={(event) => onCostChange?.(id, 'weight', event.target.value)}
                            disabled={disabled || saving || !canUpdateWeight}
                            className={`h-10 w-full rounded border px-3 text-sm ${errorsById[id] ? 'border-red-400' : 'border-gray-300'}`}
                          />
                          <span className="text-xs text-gray-500">kg</span>
                        </span>
                      </label>
                      {!canUpdateWeight && (
                        <p className="mt-1 text-[11px] text-amber-800">Necesitas permiso para editar productos.</p>
                      )}
                    </>
                  )}
                  {canEditCost && (
                    <>
                      <label className="text-xs font-semibold text-gray-700">
                        Costo de referencia
                        <input
                          aria-label={`Costo de referencia para ${rowLabel}`}
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={draft.cost ?? ''}
                          onChange={(event) => onCostChange?.(id, 'cost', event.target.value)}
                          disabled={disabled || saving || !canUpdateCost}
                          className={`mt-1 h-10 w-full rounded border px-3 text-sm ${errorsById[id] ? 'border-red-400' : 'border-gray-300'}`}
                        />
                      </label>
                      {!canUpdateCost && (
                        <p className="mt-1 text-[11px] text-amber-800">Necesitas permiso para editar precios.</p>
                      )}
                    </>
                  )}
                </div>
                <label className="text-xs font-semibold text-gray-700">
                  {ruleName} para este precio
                  <span className="mt-1 flex items-center gap-1">
                    <select
                      aria-label={`Tipo de ${ruleName.toLowerCase()} para ${rowLabel}`}
                      value={draft.ruleType || row.ruleType || 'amount'}
                      onChange={(event) => onCostChange?.(id, 'ruleType', event.target.value)}
                      disabled={disabled || saving}
                      className="h-10 rounded border border-gray-300 bg-white px-2 text-sm"
                    >
                      <option value="amount">$</option>
                      <option value="percent">%</option>
                    </select>
                    <input
                      aria-label={`${ruleName} para ${rowLabel}`}
                      type="number"
                      min="0"
                      step="any"
                      value={draft.ruleValue ?? row.ruleValue ?? ''}
                      onChange={(event) => onCostChange?.(id, 'ruleValue', event.target.value)}
                      disabled={disabled || saving}
                      className={`h-10 w-full rounded border px-3 text-sm ${errorsById[id] ? 'border-red-400' : 'border-gray-300'}`}
                    />
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => onSave?.(row)}
                  disabled={disabled || saving || !hasRequiredReference || !hasRuleValue}
                  className="h-10 rounded bg-amber-700 px-4 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
                >
                  {saving ? 'Aplicando...' : 'Guardar y aplicar'}
                </button>
              </div>
              {errorsById[id] && (
                <p className="mt-2 text-xs font-medium text-red-600">{errorsById[id]}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PendingClientPricingCosts;
